import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Building2, Pencil, Trash2, Plus, Search, Download, RefreshCw, Calculator, Wallet, Check, Filter, ArrowDown, ArrowUp, ArrowUpDown, CheckCircle, UserCircle, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import CashWizard from '../components/CashWizard';
import ExpenseWizard from '../components/ExpenseWizard';
import Tresorerie from './Tresorerie';
import ClotureCaisseTab from '../components/ClotureCaisseTab';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';
import useGranularPermissions from '../hooks/useGranularPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Caisse() {
  const { getPharmacyIdsParam } = usePharmacies();
  const { hasPermission, hasAnyPermission, canCreate, canUpdate, canDelete, canValidateCash } = useGranularPermissions();
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const [activeTab, setActiveTab] = useState(() => {
    if (hasAnyPermission('caisse.entrees')) return 'entrees';
    if (hasAnyPermission('caisse.sorties')) return 'sorties';
    if (hasPermission('caisse.tresorerie', 'view')) return 'tresorerie';
    if (hasAnyPermission('closures')) return 'cloture';
    return 'entrees';
  });
  const [loading, setLoading] = useState(true);

  // Data
  const [cashClosures, setCashClosures] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [users, setUsers] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [closureUserFilter, setClosureUserFilter] = useState('Tout');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('Tout');
  const [expenseSubcategoryFilter, setExpenseSubcategoryFilter] = useState('Tout');
  const [expenseSupplierFilter, setExpenseSupplierFilter] = useState('Tout');
  const [expensePaymentFilter, setExpensePaymentFilter] = useState('Tout');
  const [dateRange, setDateRange] = useState(() => ({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  }));
  const [repartitionPeriod, setRepartitionPeriod] = useState('Mois');
  const [evolutionPeriod, setEvolutionPeriod] = useState('Mois');

  // Modals
  const [wizardOpen, setWizardOpen] = useState(false);
  const [expenseWizardOpen, setExpenseWizardOpen] = useState(false);
  const [clotureWizardOpen, setClotureWizardOpen] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [adminUpdateDialogOpen, setAdminUpdateDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [adminFormData, setAdminFormData] = useState({ montant_reel: '', notes: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser.id };
      const startDate = dateRange.from ? dateRange.from.toISOString() : '';
      let endDate = '';
      if (dateRange.to) {
        const end = new Date(dateRange.to);
        end.setHours(23, 59, 59, 999);
        endDate = end.toISOString();
      }
      
      const pharmacyIds = getPharmacyIdsParam();
      
      const tsParam = new Date().getTime(); // Cache bypass
      const headersWithCacheBypass = { 
        ...headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      const results = await Promise.allSettled([
        axios.get(`${API}/cash-closures`, { params: { start_date: startDate, end_date: endDate, pharmacy_ids: pharmacyIds, _t: tsParam }, headers: headersWithCacheBypass }),
        axios.get(`${API}/expenses`, { params: { start_date: startDate, end_date: endDate, pharmacy_ids: pharmacyIds, _t: tsParam }, headers: headersWithCacheBypass }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/suppliers`, { headers: headersWithCacheBypass, params: { _t: tsParam } }),
        axios.get(`${API}/users`, { headers })
      ]);
      const [closuresRes, expensesRes, categoriesRes, suppliersRes, usersRes] = results;
      setCategories(categoriesRes.status === 'fulfilled' ? categoriesRes.value.data || [] : []);
      setSuppliers(suppliersRes.status === 'fulfilled' ? suppliersRes.value.data || [] : []);
      // Si /users échoue (ex: 403 en prod), mettre au minimum l'utilisateur courant
      const fetchedUsers = usersRes.status === 'fulfilled' ? usersRes.value.data || [] : [];
      setUsers(fetchedUsers.length > 0 ? fetchedUsers : (currentUser ? [currentUser] : []));

      setCashClosures(closuresRes.status === 'fulfilled' ? closuresRes.value.data || [] : []);
      // Show all expenses instead of filtering for cash only, as requested by user
      setExpenses(expensesRes.status === 'fulfilled' ? expensesRes.value.data || [] : []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Erreur chargement caisse:', error);
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser, dateRange, getPharmacyIdsParam]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const formatCurrency = (v) => new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + ' DA';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '–';

  // ─── Filters Reset ────────────────────────────────────────────────────────────
  const handleResetFilters = () => {
    setSearch('');
    setClosureUserFilter('Tout');
    setExpenseCategoryFilter('Tout');
    setExpenseSubcategoryFilter('Tout');
    setExpenseSupplierFilter('Tout');
    setExpensePaymentFilter('Tout');
    setDateRange({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) });
    toast.success('Filtres réinitialisés');
  };

  // ─── Delete ──────────────────────────────────────────────────────────────────
  const executeDelete = async () => {
    try {
      const headers = { 'X-User-Id': currentUser?.id };
      if (deleteConfirm.type === 'closure') {
        await axios.delete(`${API}/cash-closures/${deleteConfirm.id}`, { headers });
        toast.success('Clôture supprimée');
      } else {
        await axios.delete(`${API}/expenses/${deleteConfirm.id}`, { headers });
        toast.success('Dépense supprimée');
      }
      setDeleteConfirm(null);
      fetchAll();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ─── Validation (Admin Update) ───────────────────────────────────────────────
  const openAdminDialog = (entry) => {
    setSelectedEntry(entry);
    setAdminFormData({
      montant_reel: entry.montant_reel?.toString() || '',
      notes: entry.admin_notes || '',
    });
    setAdminUpdateDialogOpen(true);
  };

  const handleAdminUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEntry) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      const response = await axios.put(`${API}/cash-closures/${selectedEntry.id}/validate`, {
        montant_reel: parseFloat(adminFormData.montant_reel),
        notes: adminFormData.notes
      }, { headers });
      
      const difference = response.data.difference;
      
      toast.success(
        difference === 0 
          ? 'Caisse équilibrée !' 
          : difference > 0 
          ? `Excédent de ${formatCurrency(difference)}` 
          : `Manque de ${formatCurrency(Math.abs(difference))}`
      );
      
      setAdminUpdateDialogOpen(false);
      setSelectedEntry(null);
      setAdminFormData({ montant_reel: '', notes: '' });
      fetchAll();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé. Droits administrateur requis.');
      } else {
        toast.error('Erreur lors de la validation');
      }
    }
  };

  // ─── Wizard Submit Handlers ─────────────────────────────────────────────────
  const handleCashSubmit = async (wizardData) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id };
      const pharmacyIdsStr = getPharmacyIdsParam();
      const targetPharmacyId = pharmacyIdsStr === 'all' 
        ? (currentUser?.pharmacies?.[0]?.pharmacy_id || currentUser?.pharmacy_id || null) 
        : pharmacyIdsStr.split(',')[0];

      const payload = {
        user_id: wizardData.user_id,
        date: (() => {
          const d = wizardData.date ? parseSafeDate(wizardData.date) : new Date();
          const today = new Date();
          if (d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()) {
            return new Date().toISOString();
          }
          // Force noon local time to avoid timezone shifts when backend strips timezone offsets
          d.setHours(12, 0, 0, 0);
          return d.toISOString();
        })(),
        montant_caissier: parseFloat(wizardData.montant_caissier) || 0,
        montant_reel: (wizardData.montant_reel !== '' && wizardData.montant_reel != null) ? parseFloat(wizardData.montant_reel) : null,
        notes: wizardData.notes || '',
        pharmacy_id: targetPharmacyId
      };

      if (wizardData._editId) {
        await axios.put(`${API}/cash-closures/${wizardData._editId}`, payload, { headers });
        toast.success('Clôture mise à jour');
      } else {
        await axios.post(`${API}/cash-closures`, payload, { headers });
        toast.success('Clôture ajoutée');
      }
      setWizardOpen(false);
      setEditingClosure(null);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement de la clôture");
    }
  };

  const handleExpenseSubmit = async (wizardData) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id };
      const pharmacyIdsStr = getPharmacyIdsParam();
      const targetPharmacyId = pharmacyIdsStr === 'all' 
        ? (currentUser?.pharmacies?.[0]?.pharmacy_id || currentUser?.pharmacy_id || null) 
        : pharmacyIdsStr.split(',')[0];
      
      const isEdit = !!wizardData._editId;
      const editCategoryId = wizardData._editCategoryId;

      let categoryId;
      const categoryMapping = {
        'Achat': ['Achats', 'Achat'],
        'Charge': ['Charges', 'Charge'],
        'Salaire': ['Salaires', 'Salaire']
      };
      const possibleNames = categoryMapping[wizardData.category || 'Achat'] || [wizardData.category || 'Achat'];
      const selectedCategory = categories.find(c => 
        c.name && possibleNames.some(name => name && c.name.toLowerCase().includes(name.toLowerCase()))
      );
      
      if (selectedCategory) {
        categoryId = selectedCategory.id;
      }
      // Si la catégorie n'existe pas, on utilise directement le nom (le backend l'accepte)
      if (!categoryId) {
        categoryId = wizardData.category || 'Achat';
      }

      let supplierId = null;
      let supplierName = wizardData.supplier || '';
      if (supplierName) {
        const selectedSupplier = suppliers.find(s => s.name === supplierName);
        if (selectedSupplier) {
          supplierId = selectedSupplier.id;
        }
      }

      const payload = {
        date: (() => {
          const d = wizardData.date ? parseSafeDate(wizardData.date) : new Date();
          const today = new Date();
          if (d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()) {
            return new Date().toISOString();
          }
          // Force noon local time to avoid timezone shifts when backend strips timezone offsets
          d.setHours(12, 0, 0, 0);
          return d.toISOString();
        })(),
        amount: parseFloat(wizardData.amount),
        category_id: categoryId,
        subcategory: wizardData.subcategory || null,
        description: wizardData.description || '',
        payment_method: wizardData.payment_method || 'cash',
        pay_state: wizardData.pay_state || 'paid',
        supplier: wizardData.supplier || '',
        invoice_number: wizardData.invoice_number || '',
        pharmacy_id: targetPharmacyId
      };

      if (wizardData._editId) {
        await axios.put(`${API}/expenses/${wizardData._editId}`, payload, { headers });
        toast.success('Dépense mise à jour');
      } else {
        await axios.post(`${API}/expenses`, payload, { headers });
        
        // If it's a payment linked to an invoice, also record it as a supplier payment
        if (wizardData.expenseType === 'invoice' && wizardData.document) {
          try {
            const targetSupplierId = supplierId || wizardData.document.supplier_id;
            const paymentPayload = {
              supplier_id: targetSupplierId,
              invoice_id: wizardData.document.id,
              amount: payload.amount,
              date: payload.date,
              reference: payload.invoice_number || '',
              notes: payload.description || 'Paiement enregistré depuis la caisse'
            };
            if (targetSupplierId) {
              await axios.post(`${API}/suppliers/${targetSupplierId}/payments`, paymentPayload, { headers });
            } else {
              console.warn("No supplier ID found, cannot update invoice status automatically.");
              toast.error('La facture ne peut pas être marquée comme payée (fournisseur introuvable).');
            }
          } catch (paymentErr) {
            console.error('Erreur lors de la création du paiement fournisseur:', paymentErr);
            toast.error('La dépense a été créée, mais la facture n\'a pas pu être marquée comme payée.');
          }
        }
        
        toast.success('Dépense ajoutée');
      }
      setExpenseWizardOpen(false);
      setEditingExpense(null);
      fetchAll();
    } catch (error) {
      console.error(error);
      if (error.response?.data?.detail) {
        toast.error(`Erreur: ${JSON.stringify(error.response.data.detail)}`);
      } else {
        toast.error("Erreur lors de l'enregistrement de la dépense");
      }
    }
  };

  // ─── Tabs Config ──────────────────────────────────────────────────────────────
  const tabs = [
    { key: 'entrees', label: 'ENTRÉES CAISSE', sub: 'Clôtures de caisse', icon: <TrendingUp className="w-5 h-5" />, color: 'green' },
    { key: 'sorties', label: 'SORTIES CAISSE', sub: 'Dépenses en espèces', icon: <TrendingDown className="w-5 h-5" />, color: 'red' },
    { key: 'tresorerie', label: 'TRÉSORERIE CAISSE', sub: 'Vue globale', icon: <Building2 className="w-5 h-5" />, color: 'blue' },
  ];

  const canViewAllClosures = (currentUser?.role && ['super_admin', 'admin', 'gerant', 'owner'].includes(currentUser.role.toLowerCase())) 
    || hasPermission('closures', 'view_all_users') 
    || hasPermission('caisse.entrees', 'view_all_users');

  const uniqueClosureUsers = useMemo(() => {
    const userMap = new Map();
    cashClosures.forEach(c => {
      const id = c.user_id || c.username;
      const name = c.user_fullname || c.username || 'Inconnu';
      if (id && !userMap.has(id)) {
        userMap.set(id, { id, name });
      }
    });
    return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [cashClosures]);

  // Filtered data based on search and user filter
  const filteredClosures = cashClosures.filter(c => {
    const matchesSearch = 
      (c.username || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.user_fullname || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.notes || '').toLowerCase().includes(search.toLowerCase());

    const matchesUser = closureUserFilter === 'Tout' || 
      c.user_id === closureUserFilter || 
      c.username === closureUserFilter || 
      c.user_fullname === closureUserFilter;

    return matchesSearch && matchesUser;
  });

  const [closuresSortConfig, setClosuresSortConfig] = useState({ key: 'date', direction: 'desc' });
  const handleClosuresSort = (key) => {
    setClosuresSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedClosures = useMemo(() => {
    let sortableItems = [...filteredClosures];
    if (closuresSortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = 0, valB = 0;
        if (closuresSortConfig.key === 'date') {
          valA = a.date ? parseSafeDate(a.date).getTime() : 0;
          valB = b.date ? parseSafeDate(b.date).getTime() : 0;
        } else if (closuresSortConfig.key === 'user') {
          valA = (a.user_fullname || a.username || '').toLowerCase();
          valB = (b.user_fullname || b.username || '').toLowerCase();
        } else if (closuresSortConfig.key === 'mt_caissier') {
          valA = a.montant_caissier || 0;
          valB = b.montant_caissier || 0;
        } else if (closuresSortConfig.key === 'mt_valide') {
          valA = a.montant_reel || 0;
          valB = b.montant_reel || 0;
        } else if (closuresSortConfig.key === 'difference') {
          valA = a.status === 'validated' ? (a.montant_reel || 0) - (a.montant_caissier || 0) : -999999999;
          valB = b.status === 'validated' ? (b.montant_reel || 0) - (b.montant_caissier || 0) : -999999999;
        } else if (closuresSortConfig.key === 'status') {
          valA = a.status || '';
          valB = b.status || '';
        }
        if (valA < valB) return closuresSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return closuresSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredClosures, closuresSortConfig]);

  const SortableClosureHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = closuresSortConfig.key === sortKey;
    return (
      <TableHead
        onClick={() => handleClosuresSort(sortKey)}
        className={`whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className="inline-flex items-center gap-1">
          <span>{label}</span>
          {isSorted ? (
            closuresSortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </TableHead>
    );
  };

  
  const uniqueCategories = [...new Set(expenses.map(e => e.category_name).filter(Boolean))].sort();
  const uniqueSubcategories = [...new Set(expenses.map(e => e.subcategory).filter(Boolean))].sort();
  const uniqueSuppliers = [...new Set(expenses.map(e => e.supplier).filter(Boolean))].sort();
  const uniquePayments = [...new Set(expenses.map(e => e.payment_method).filter(Boolean))].sort();
  
  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = 
      (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.category_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.supplier || '').toLowerCase().includes(search.toLowerCase());
      
    const matchesCat = expenseCategoryFilter === 'Tout' || e.category_name === expenseCategoryFilter;
    const matchesSub = expenseSubcategoryFilter === 'Tout' || e.subcategory === expenseSubcategoryFilter;
    const matchesSup = expenseSupplierFilter === 'Tout' || e.supplier === expenseSupplierFilter;
    const matchesPay = expensePaymentFilter === 'Tout' || e.payment_method === expensePaymentFilter;
    
    return matchesSearch && matchesCat && matchesSub && matchesSup && matchesPay;
  });

  const [expensesSortConfig, setExpensesSortConfig] = useState({ key: 'date', direction: 'desc' });
  const handleExpensesSort = (key) => {
    setExpensesSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedExpenses = useMemo(() => {
    let sortableItems = [...filteredExpenses];
    if (expensesSortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = 0, valB = 0;
        if (expensesSortConfig.key === 'date') {
          valA = a.date ? parseSafeDate(a.date).getTime() : 0;
          valB = b.date ? parseSafeDate(b.date).getTime() : 0;
        } else if (expensesSortConfig.key === 'user') {
          valA = (a.user_fullname || a.username || '').toLowerCase();
          valB = (b.user_fullname || b.username || '').toLowerCase();
        } else if (expensesSortConfig.key === 'description') {
          valA = (a.description || '').toLowerCase();
          valB = (b.description || '').toLowerCase();
        } else if (expensesSortConfig.key === 'paiement') {
          valA = (a.payment_method || '').toLowerCase();
          valB = (b.payment_method || '').toLowerCase();
        } else if (expensesSortConfig.key === 'etat') {
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
        } else if (expensesSortConfig.key === 'montant') {
          valA = a.amount || 0;
          valB = b.amount || 0;
        } else if (expensesSortConfig.key === 'categorie') {
          valA = (a.category_name || '').toLowerCase();
          valB = (b.category_name || '').toLowerCase();
        } else if (expensesSortConfig.key === 'sous_categorie') {
          valA = (a.subcategory || '').toLowerCase();
          valB = (b.subcategory || '').toLowerCase();
        } else if (expensesSortConfig.key === 'fournisseur') {
          valA = (a.supplier || '').toLowerCase();
          valB = (b.supplier || '').toLowerCase();
        }

        if (valA < valB) return expensesSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return expensesSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredExpenses, expensesSortConfig]);

  const SortableExpenseHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = expensesSortConfig.key === sortKey;
    return (
      <TableHead
        onClick={() => handleExpensesSort(sortKey)}
        className={`whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className={`inline-flex items-center gap-1 ${className.includes('text-right') ? 'justify-end w-full' : ''}`}>
          <span>{label}</span>
          {isSorted ? (
            expensesSortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </TableHead>
    );
  };

  const handleExport = (exportFormat) => {
    let data = [];
    let columns = [];
    let title = '';
    let filename = '';

    if (activeTab === 'entrees') {
      data = filteredClosures;
      columns = [
        { header: 'Date', dataKey: 'date' },
        { header: 'Caissier', dataKey: 'username' },
        { header: 'Montant Déclaré (DZD)', dataKey: 'montant_caissier' },
        { header: 'Montant Validé (DZD)', dataKey: 'montant_reel' },
        { header: 'Différence (DZD)', dataKey: 'difference' },
        { header: 'Notes', dataKey: 'notes' },
      ];
      title = 'Rapport des Entrées (Fermetures de Caisse)';
      filename = 'Entrees_Caisse';
    } else if (activeTab === 'sorties') {
      data = filteredExpenses;
      columns = [
        { header: 'Date', dataKey: 'date' },
        { header: 'Catégorie', dataKey: 'category_name' },
        { header: 'Fournisseur', dataKey: 'supplier' },
        { header: 'Description', dataKey: 'description' },
        { header: 'Montant (DZD)', dataKey: 'amount' },
      ];
      title = 'Rapport des Sorties (Dépenses)';
      filename = 'Sorties_Caisse';
    } else {
      return;
    }

    const formattedData = data.map(item => ({
      ...item,
      date: item.date ? format(parseSafeDate(item.date), 'dd/MM/yyyy HH:mm') : '',
    }));

    if (exportFormat === 'csv') exportToCSV(formattedData, filename, columns);
    else if (exportFormat === 'excel') exportToExcel(formattedData, filename, columns);
    else if (exportFormat === 'pdf') exportToPDF(formattedData, filename, columns, title);
  };

  // Kpis Entrées
  const totalDeclare = filteredClosures.reduce((sum, c) => sum + (c.montant_caissier || 0), 0);
  const totalValide = filteredClosures.filter(c => c.status === 'validated').reduce((sum, c) => sum + (c.montant_reel || 0), 0);
  const difference = filteredClosures.filter(c => c.status === 'validated').reduce((sum, c) => sum + (c.montant_caissier || 0), 0) - totalValide;

  const entreeKpis = [
    { label: 'Total Déclaré', value: formatCurrency(totalDeclare), sub: 'Par les caissiers', icon: <Wallet className="w-5 h-5 text-blue-600" />, color: 'blue' },
    { label: 'Total Validé', value: formatCurrency(totalValide), sub: 'Par les admins', icon: <Check className="w-5 h-5 text-purple-600" />, color: 'purple' },
    { label: 'Différence', value: formatCurrency(difference), sub: difference > 0 ? 'Excédent' : 'Manquant', icon: <Calculator className="w-5 h-5 text-green-600" />, color: 'green' },
  ];

  // Kpis Sorties
  const filteredKPIs = React.useMemo(() => {
    let total = 0, charges = 0, achats = 0, salaires = 0;
    filteredExpenses.forEach(exp => {
      let amt = 0;
      if (exp.pay_state === 'paid') {
        amt = exp.amount || 0;
      } else if (exp.pay_state === 'partial') {
        amt = exp.paid_amount || 0;
      } else {
        amt = 0; // unpaid
      }
      total += amt;
      const parent = (exp.parent_category || '').toLowerCase();
      const catName = (exp.category_name || exp.category_id || exp.category || '').toLowerCase();
      const desc = (exp.description || '').toLowerCase();

      if (parent.includes('salaire') || catName.includes('salaire') || catName.includes('paie') || catName.includes('remuneration') || desc.includes('salaire')) {
        salaires += amt;
      } else if (parent.includes('charge') || catName.includes('charge')) {
        charges += amt;
      } else if (parent.includes('achat') || catName.includes('achat')) {
        achats += amt;
      } else {
        achats += amt; // default
      }
    });
    return { total, charges, achats, salaires };
  }, [filteredExpenses]);

  const kpiColorMap = {
    green: 'bg-green-50 text-green-600', blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600', orange: 'bg-orange-50 text-orange-600', purple: 'bg-purple-50 text-purple-600',
  };

  // Rendering Tresorerie Tab (integrated into main layout)

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-3 sm:p-6 bg-gray-50 min-h-screen">

      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Cash</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            {activeTab === 'entrees' && 'Gérez et suivez les flux entrants de vos caisses'}
            {activeTab === 'sorties' && 'Gérez et suivez les dépenses et sorties de cash'}
            {activeTab === 'tresorerie' && 'Analyse globale des flux et équilibre financier'}
            {activeTab === 'cloture' && 'Contrôlez, validez et clôturez vos périodes de caisse'}
          </p>
        </div>

      </div>

      {/* Filtres + Actions */}
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0" />
          <div className="flex items-center gap-3 flex-wrap">
            <ExpenseDateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} compact={true} />
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors"
              title="Réinitialiser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm bg-gray-100 text-gray-700 border border-gray-200 rounded-lg px-4 py-2.5 hover:bg-gray-200 transition-colors">
                  <Download className="w-4 h-4" /> Exporter
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                  Exporter en PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                  Exporter en Excel (XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                  Exporter en CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {activeTab === 'entrees' && (
              <button
                onClick={() => {
                  if (canCreate('caisse.entrees') || canCreate('closures')) {
                    setEditingClosure(null);
                    setWizardOpen(true);
                  }
                }}
                disabled={!(canCreate('caisse.entrees') || canCreate('closures'))}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 shadow-sm ${
                  (canCreate('caisse.entrees') || canCreate('closures'))
                    ? 'bg-white text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                }`}
                title={!(canCreate('caisse.entrees') || canCreate('closures')) ? 'Permission refusée' : ''}
              >
                <Plus className="w-5 h-5" />
                Nouvelle Entrée
              </button>
            )}
            {activeTab === 'sorties' && (
              <button
                onClick={() => {
                  if (canCreate('caisse.sorties') || canCreate('expenses')) {
                    setEditingExpense(null);
                    setExpenseWizardOpen(true);
                  }
                }}
                disabled={!(canCreate('caisse.sorties') || canCreate('expenses'))}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 shadow-sm ${
                  (canCreate('caisse.sorties') || canCreate('expenses'))
                    ? 'bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                }`}
                title={!(canCreate('caisse.sorties') || canCreate('expenses')) ? 'Permission refusée' : ''}
              >
                <Plus className="w-5 h-5" />
                Nouvelle Sortie
              </button>
            )}
            {activeTab === 'cloture' && (
              <button
                onClick={() => canCreate('closures') && setClotureWizardOpen(true)}
                disabled={!canCreate('closures')}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 shadow-sm ${
                  canCreate('closures')
                    ? 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                }`}
                title={!canCreate('closures') ? 'Permission refusée' : ''}
              >
                <Plus className="w-5 h-5" />
                Nouvelle clôture
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Toggle Entrées / Sorties / Trésorerie */}
      <div className="flex items-center gap-3 flex-wrap">
        {hasAnyPermission('caisse.entrees') && (
          <button
            onClick={() => setActiveTab('entrees')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
              activeTab === 'entrees'
                ? 'bg-green-600 text-white border-green-600 shadow-lg'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-200'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            Entrées Cash
          </button>
        )}
        {hasAnyPermission('caisse.sorties') && (
          <button
            onClick={() => setActiveTab('sorties')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
              activeTab === 'sorties'
                ? 'bg-red-600 text-white border-red-600 shadow-lg'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-red-50 hover:border-red-200'
            }`}
          >
            <TrendingDown className="w-5 h-5" />
            Sorties Cash
          </button>
        )}
        {hasPermission('caisse.tresorerie', 'view') && (
          <button
            onClick={() => setActiveTab('tresorerie')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
              activeTab === 'tresorerie'
                ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-blue-50 hover:border-blue-200'
            }`}
          >
            <Building2 className="w-5 h-5" />
            Trésorerie Cash
          </button>
        )}
        {hasAnyPermission('closures') && (
          <button
            onClick={() => setActiveTab('cloture')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
              activeTab === 'cloture'
                ? 'bg-orange-500 text-white border-orange-500 shadow-lg'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-orange-50 hover:border-orange-200'
            }`}
          >
            <Wallet className="w-5 h-5" />
            Clôture caisse
          </button>
        )}
      </div>

      {activeTab === 'cloture' ? (
        <ClotureCaisseTab 
          headers={{ 'X-User-Id': currentUser?.id }} 
          currentUser={currentUser} 
          pharmacyIds={getPharmacyIdsParam()} 
          dateRange={dateRange}
          wizardOpen={clotureWizardOpen}
          setWizardOpen={setClotureWizardOpen}
        />
      ) : activeTab === 'tresorerie' ? (
        <div className="-mx-3 sm:-mx-6 -mt-4">
          <Tresorerie hideHeader={true} dateRange={dateRange} />
        </div>
      ) : (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-6">

        {/* Search Bar Row */}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-gray-800">
              {activeTab === 'entrees' ? 'Entrées Cash par Utilisateur' : 'Dépenses en Espèces'}
            </h2>
            {activeTab === 'entrees' && !canViewAllClosures && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                <UserCircle className="w-3.5 h-3.5" />
                Vue personnelle ({currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : currentUser?.username})
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
            {activeTab === 'entrees' && canViewAllClosures && uniqueClosureUsers.length > 1 && (
              <select
                value={closureUserFilter}
                onChange={e => setClosureUserFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700"
              >
                <option value="Tout">Tous les caissiers ({uniqueClosureUsers.length})</option>
                {uniqueClosureUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-full sm:w-64 pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* KPIs */}
        {activeTab === 'entrees' ? (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
            <Card className="stat-card border-l-4 border-l-blue-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-blue-500" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Montant Clôture Cash</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalDeclare)}</div>
                <p className="text-xs text-gray-400 mt-2">Total déclaré par les caissiers</p>
              </CardContent>
            </Card>

            <Card className="stat-card border-l-4 border-l-purple-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-purple-500" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Montant Théorique</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(totalValide)}</div>
                <p className="text-xs text-gray-400 mt-2">Validé par les admins</p>
              </CardContent>
            </Card>

            <Card
              className={`stat-card border-l-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer ${
                difference === 0
                  ? 'border-l-green-500'
                  : difference > 0
                  ? 'border-l-blue-500'
                  : 'border-l-red-500'
              }`}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    difference === 0 ? 'bg-green-50' : difference > 0 ? 'bg-blue-50' : 'bg-red-50'
                  }`}>
                    <Calculator
                      className={`w-5 h-5 ${
                        difference === 0
                          ? 'text-green-500'
                          : difference > 0
                          ? 'text-blue-500'
                          : 'text-red-500'
                      }`}
                    />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Différence</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    difference === 0
                      ? 'text-green-600'
                      : difference > 0
                      ? 'text-blue-600'
                      : 'text-red-600'
                  }`}
                >
                  {formatCurrency(difference)}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {difference === 0
                    ? 'Caisse équilibrée'
                    : difference > 0
                    ? 'Excédent (Plus)'
                    : 'Manque'}
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
            {/* Carte 1: Total Dépenses */}
            <Card className="stat-card border-l-4 border-l-teal-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-teal-600" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Total Dépenses</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-teal-600">
                  {(filteredKPIs.total || 0).toLocaleString('fr-DZ')} DA
                </div>
                <div className="flex items-center gap-1 text-xs mt-1">
                  <ArrowUpRight className="w-3.5 h-3.5 text-teal-600" />
                  <span className="font-semibold text-teal-600">+15,2%</span>
                  <span className="text-gray-500">vs mois dernier</span>
                </div>
              </CardContent>
            </Card>

            {/* Carte 2: Charges */}
            <Card className="stat-card border-l-4 border-l-orange-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Charges</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {(filteredKPIs.charges || 0).toLocaleString('fr-DZ')} DA
                </div>
                <div className="flex items-center gap-1 text-xs mt-1">
                  <ArrowDownRight className="w-3.5 h-3.5 text-green-600" />
                  <span className="font-semibold text-green-600">-7,1%</span>
                  <span className="text-gray-500">vs mois dernier</span>
                </div>
              </CardContent>
            </Card>

            {/* Carte 3: Achats */}
            <Card className="stat-card border-l-4 border-l-blue-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Achats</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {(filteredKPIs.achats || 0).toLocaleString('fr-DZ')} DA
                </div>
                <div className="flex items-center gap-1 text-xs mt-1">
                  <ArrowUpRight className="w-3.5 h-3.5 text-blue-600" />
                  <span className="font-semibold text-blue-600">+28,3%</span>
                  <span className="text-gray-500">vs mois dernier</span>
                </div>
              </CardContent>
            </Card>

            {/* Carte 4: Salaires */}
            <Card className="stat-card border-l-4 border-l-purple-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-600">Salaires</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  {(filteredKPIs.salaires || 0).toLocaleString('fr-DZ')} DA
                </div>
                <div className="flex items-center gap-1 text-xs mt-1">
                  <ArrowUpRight className="w-3.5 h-3.5 text-purple-600" />
                  <span className="font-semibold text-purple-600">+5,3%</span>
                  <span className="text-gray-500">vs mois dernier</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Colonne 1: Répartition des dépenses (Donut Chart) */}
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-900">Répartition des dépenses</CardTitle>
                  <div className="flex gap-4 text-sm">
                    {['Jour', 'Semaine', 'Mois', 'Année'].map((p) => (
                      <button
                        key={p}
                        onClick={() => setRepartitionPeriod(p)}
                        className={repartitionPeriod === p
                          ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600'
                          : 'text-gray-400 hover:text-gray-700'}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const CHART_COLORS = ['#10b981', '#fbbf24', '#3b82f6', '#a855f7', '#ef4444', '#f97316', '#06b6d4', '#ec4899'];
                  
                  // Filtrer les dépenses par période sélectionnée
                  const now = new Date();
                  const filteredByPeriod = filteredExpenses.filter(exp => {
                    const d = parseSafeDate(exp.date);
                    if (repartitionPeriod === 'Jour') return isSameDay(d, now);
                    if (repartitionPeriod === 'Semaine') {
                      // Semaine Samedi→Vendredi
                      const day = now.getDay();
                      const diffToSat = day === 6 ? 0 : day + 1;
                      const weekStart = new Date(now); weekStart.setDate(now.getDate() - diffToSat); weekStart.setHours(0,0,0,0);
                      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);
                      return d >= weekStart && d <= weekEnd;
                    }
                    if (repartitionPeriod === 'Mois') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                    if (repartitionPeriod === 'Année') return d.getFullYear() === now.getFullYear();
                    return true;
                  });
                  
                  // Grouper par parent_category
                  const groups = {};
                  filteredByPeriod.forEach(exp => {
                    const key = exp.parent_category || 'Autres';
                    groups[key] = (groups[key] || 0) + exp.amount;
                  });
                  const total = Object.values(groups).reduce((s, v) => s + v, 0);
                  const pieData = Object.entries(groups).map(([name, value], i) => ({
                    name,
                    value: total > 0 ? Math.round(value / total * 1000) / 10 : 0,
                    amount: value,
                    color: CHART_COLORS[i % CHART_COLORS.length]
                  }));
                  const topEntry = pieData.length > 0 ? pieData.reduce((a, b) => a.value > b.value ? a : b) : null;
                  
                  return (
                    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                      <div className="flex-shrink-0 w-[160px] h-[160px] sm:w-[200px] sm:h-[200px]">
                        {pieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(val) => `${val}%`} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Aucune donnée</div>
                        )}
                        {topEntry && (
                          <div className="relative -mt-28 sm:-mt-32 text-center pointer-events-none">
                            <div className="text-xl sm:text-2xl font-bold text-gray-900">{topEntry.value}%</div>
                            <div className="text-xs sm:text-sm text-gray-500">{total.toLocaleString('fr-DZ')} DA</div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        {pieData.map((entry, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                              <span className="text-sm text-gray-700">{entry.name}</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">{entry.value}%</span>
                          </div>
                        ))}
                        {pieData.length === 0 && <p className="text-sm text-gray-400">Aucune dépense pour cette période</p>}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Colonne 2: Évolution (Bar Chart) */}
            <Card className="border-0 shadow-lg rounded-2xl bg-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-900">Évolution</CardTitle>
                  <div className="flex gap-4 text-sm">
                    {['Jour', 'Semaine', 'Mois'].map((p) => (
                      <button
                        key={p}
                        onClick={() => setEvolutionPeriod(p)}
                        className={evolutionPeriod === p
                          ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600'
                          : 'text-gray-400 hover:text-gray-700'}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Agréger les dépenses dans la date range sélectionnée
                  const barData = [];
                  if (expenses.length > 0 && dateRange?.from && dateRange?.to) {
                    const start = new Date(dateRange.from);
                    const end = new Date(dateRange.to);
                    
                    if (evolutionPeriod === 'Jour') {
                      // Grouper par jour
                      try {
                        const days = eachDayOfInterval({ start, end });
                        days.forEach(day => {
                          const dayTotal = expenses.filter(e => isSameDay(parseSafeDate(e.date), day)).reduce((s, e) => s + e.amount, 0);
                          if (dayTotal > 0 || days.length <= 31) {
                            barData.push({ date: format(day, 'dd/MM', { locale: fr }), amount: dayTotal });
                          }
                        });
                      } catch {}
                    } else if (evolutionPeriod === 'Semaine') {
                      // Grouper par semaine (Samedi→Vendredi)
                      const grouped = {};
                      filteredExpenses.forEach(exp => {
                        const d = parseSafeDate(exp.date);
                        const day = d.getDay();
                        const diffToSat = day === 6 ? 0 : day + 1;
                        const weekStart = new Date(d); weekStart.setDate(d.getDate() - diffToSat); weekStart.setHours(0,0,0,0);
                        const key = format(weekStart, 'dd/MM');
                        grouped[key] = (grouped[key] || 0) + exp.amount;
                      });
                      Object.entries(grouped).sort().forEach(([k, v]) => barData.push({ date: `S. ${k}`, amount: v }));
                    } else {
                      // Grouper par mois
                      const grouped = {};
                      filteredExpenses.forEach(exp => {
                        const d = parseSafeDate(exp.date);
                        const key = format(d, 'MMM yyyy', { locale: fr });
                        const sortKey = format(d, 'yyyy-MM');
                        if (!grouped[sortKey]) grouped[sortKey] = { date: key, amount: 0 };
                        grouped[sortKey].amount += exp.amount;
                      });
                      Object.keys(grouped).sort().forEach(k => barData.push(grouped[k]));
                    }
                  }
                  
                  return (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                          <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                            formatter={(value) => `${value.toLocaleString('fr-DZ')} DA`}
                          />
                          <Bar dataKey="amount" fill="#10b981" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      {barData.length === 0 && (
                        <div className="text-center py-4 text-gray-400 text-sm">Aucune donnée pour cette période</div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
          </>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {activeTab === 'entrees' ? (
                    <>
                      <SortableClosureHeader label="Date" sortKey="date" />
                      <SortableClosureHeader label="Utilisateur" sortKey="user" />
                      <SortableClosureHeader label="Mt Caissier" sortKey="mt_caissier" />
                      <SortableClosureHeader label="Mt Validé" sortKey="mt_valide" />
                      <SortableClosureHeader label="Différence" sortKey="difference" />
                      <SortableClosureHeader label="Statut" sortKey="status" />
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</TableHead>
                    </>
                  ) : (
                    <>
                      <SortableExpenseHeader label="Date" sortKey="date" />
                      <SortableExpenseHeader label="Utilisateur" sortKey="user" />
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors outline-none group select-none">
                              <span className="font-semibold">Catégorie</span>
                              {expenseCategoryFilter !== 'Tout' ? (
                                <Filter className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Filter className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
                            <DropdownMenuItem onClick={() => setExpenseCategoryFilter('Tout')}>
                              <span className={expenseCategoryFilter === 'Tout' ? 'font-bold' : ''}>Toutes</span>
                            </DropdownMenuItem>
                            {uniqueCategories.map(c => (
                              <DropdownMenuItem key={c} onClick={() => setExpenseCategoryFilter(c)}>
                                <span className={expenseCategoryFilter === c ? 'font-bold' : ''}>{c}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors outline-none group select-none">
                              <span className="font-semibold">Sous-catégorie</span>
                              {expenseSubcategoryFilter !== 'Tout' ? (
                                <Filter className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Filter className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
                            <DropdownMenuItem onClick={() => setExpenseSubcategoryFilter('Tout')}>
                              <span className={expenseSubcategoryFilter === 'Tout' ? 'font-bold' : ''}>Toutes</span>
                            </DropdownMenuItem>
                            {uniqueSubcategories.map(c => (
                              <DropdownMenuItem key={c} onClick={() => setExpenseSubcategoryFilter(c)}>
                                <span className={expenseSubcategoryFilter === c ? 'font-bold' : ''}>{c}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableHead>
                      <SortableExpenseHeader label="Description" sortKey="description" />
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors outline-none group select-none">
                              <span className="font-semibold">Fournisseur</span>
                              {expenseSupplierFilter !== 'Tout' ? (
                                <Filter className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Filter className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
                            <DropdownMenuItem onClick={() => setExpenseSupplierFilter('Tout')}>
                              <span className={expenseSupplierFilter === 'Tout' ? 'font-bold' : ''}>Tous</span>
                            </DropdownMenuItem>
                            {uniqueSuppliers.map(c => (
                              <DropdownMenuItem key={c} onClick={() => setExpenseSupplierFilter(c)}>
                                <span className={expenseSupplierFilter === c ? 'font-bold' : ''}>{c}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <div className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors outline-none group select-none">
                              <span className="font-semibold">Paiement</span>
                              {expensePaymentFilter !== 'Tout' ? (
                                <Filter className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Filter className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto">
                            <DropdownMenuItem onClick={() => setExpensePaymentFilter('Tout')}>
                              <span className={expensePaymentFilter === 'Tout' ? 'font-bold' : ''}>Tous</span>
                            </DropdownMenuItem>
                            {uniquePayments.map(p => {
                              const label = p === 'card' ? 'Carte' : p === 'transfer' ? 'Virement' : p === 'check' ? 'Chèque' : p === 'traite' ? 'Traite' : 'Espèces';
                              return (
                                <DropdownMenuItem key={p} onClick={() => setExpensePaymentFilter(p)}>
                                  <span className={expensePaymentFilter === p ? 'font-bold' : ''}>{label}</span>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableHead>
                      <SortableExpenseHeader label="État" sortKey="etat" />
                      <SortableExpenseHeader label="Montant" sortKey="montant" className="text-right" />
                      <TableHead className="whitespace-nowrap text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeTab === 'entrees' ? (
                  sortedClosures.map(c => {
                    const diff = (c.montant_caissier || 0) - (c.montant_reel || 0);
                    return (
                      <TableRow key={c.id} className="hover:bg-gray-50 border-b border-gray-100">
                        <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(c.date)}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-semibold">
                          {c.user_fullname || c.username || 'Inconnu'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-black text-blue-600">{formatCurrency(c.montant_caissier)}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-black text-purple-600">{c.status === 'validated' ? formatCurrency(c.montant_reel) : '–'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {c.status === 'validated' ? (
                            <span className={`px-2 py-1 rounded text-xs font-bold ${diff === 0 ? 'bg-green-100 text-green-700' : diff > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                              {formatCurrency(diff)}
                            </span>
                          ) : '–'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge className={c.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                            {c.status === 'validated' ? 'Validé' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            {c.status !== 'validated' && canValidateCash() && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAdminDialog(c)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                Valider
                              </Button>
                            )}
                            {((canUpdate('caisse.entrees') || canUpdate('closures')) && (c.status !== 'validated' || ['super_admin', 'admin', 'gerant', 'owner'].includes(currentUser?.role?.toLowerCase()))) && (
                              <button
                                className="p-1.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors"
                                onClick={() => { setEditingClosure(c); setWizardOpen(true); }}
                                title="Modifier"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {((canDelete('caisse.entrees') || canDelete('closures')) && (c.status !== 'validated' || ['super_admin', 'admin', 'gerant', 'owner'].includes(currentUser?.role?.toLowerCase()))) && (
                              <button
                                className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                                onClick={() => setDeleteConfirm({ type: 'closure', id: c.id })}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  sortedExpenses.map(e => (
                    <TableRow key={e.id} className="hover:bg-gray-50 border-b border-gray-100">
                      <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(e.date)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                            {(e.created_by_fullname || e.created_by_username || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-700">{e.created_by_fullname || e.created_by_username || 'Inconnu'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge className="bg-gray-100 text-gray-700">{e.category_name || 'Divers'}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-gray-600">{e.subcategory || '–'}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-gray-600">{e.description || '–'}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-gray-500">{e.supplier || '–'}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge className="bg-gray-100 text-gray-600">
                          {e.payment_method === 'card' ? 'Carte' : 
                           e.payment_method === 'transfer' ? 'Virement' : 
                           e.payment_method === 'check' ? 'Chèque' : 
                           e.payment_method === 'traite' ? 'Traite' : 'Espèces'}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge className={
                          e.pay_state === 'paid' ? 'bg-green-100 text-green-700' :
                          e.pay_state === 'partial' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {e.pay_state === 'paid' ? 'Payée' :
                           e.pay_state === 'partial' ? 'Partielle' : 'Non payée'}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm">
                        <div className="font-black text-red-600">{formatCurrency(e.amount)}</div>
                        {e.pay_state === 'partial' && e.paid_amount != null && (
                          <div className="text-xs text-orange-600 font-semibold mt-1">Payé: {formatCurrency(e.paid_amount)}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate('caisse.sorties') && (
                            <button
                              className="p-1.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors"
                              onClick={() => { setEditingExpense(e); setExpenseWizardOpen(true); }}
                              title="Modifier"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete('caisse.sorties') && (
                            <button
                              className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                              onClick={() => setDeleteConfirm({ type: 'expense', id: e.id })}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {(activeTab === 'entrees' && filteredClosures.length === 0) || (activeTab === 'sorties' && filteredExpenses.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-gray-400">
                      Aucune donnée trouvée
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      )}

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">Êtes-vous sûr de vouloir supprimer cet élément ?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={executeDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {wizardOpen && (
        <CashWizard
          isOpen={wizardOpen}
          onClose={() => { setWizardOpen(false); setEditingClosure(null); fetchAll(); }}
          onSubmit={handleCashSubmit}
          currentUser={currentUser}
          users={users}
          editMode={!!editingClosure}
          initialData={editingClosure}
        />
      )}
      {expenseWizardOpen && (
        <ExpenseWizard
          isOpen={expenseWizardOpen}
          onClose={() => { setExpenseWizardOpen(false); setEditingExpense(null); fetchAll(); }}
          onSubmit={handleExpenseSubmit}
          categories={categories.filter(c => c.type === 'expense')}
          suppliers={suppliers}
          currentUser={currentUser}
          initialData={editingExpense || { payment_method: 'cash' }}
          editMode={!!editingExpense}
        />
      )}
      <Dialog open={adminUpdateDialogOpen} onOpenChange={setAdminUpdateDialogOpen}>
        <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-white/40 shadow-2xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Validation Caisse
              </h2>
              <p className="text-blue-100 mt-2 flex items-center gap-2 opacity-90">
                <UserCircle className="w-4 h-4" />
                Caissier: {selectedEntry?.username || 'Admin'}
              </p>
            </div>
            {/* abstract background circles */}
            <div className="absolute -right-4 -top-12 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute right-12 -bottom-8 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div className="p-6">
            <form onSubmit={handleAdminUpdate} className="space-y-5">
              <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Montant Caissier</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(selectedEntry?.montant_caissier)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="montant_reel" className="text-sm font-semibold text-gray-700">Montant Réel (DA) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Wallet className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="montant_reel"
                    type="number"
                    step="0.01"
                    min="0"
                    value={adminFormData.montant_reel}
                    onChange={(e) => setAdminFormData({ ...adminFormData, montant_reel: e.target.value })}
                    required
                    className="pl-10 h-11 border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Saisissez le montant réel"
                  />
                </div>
              </div>

              {adminFormData.montant_reel && (
                <div className={`p-4 border rounded-xl transition-all duration-300 ${
                    selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? 'bg-emerald-50/50 border-emerald-100'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? 'bg-blue-50/50 border-blue-100'
                      : 'bg-rose-50/50 border-rose-100'
                  }`}>
                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Différence calculée</p>
                  <div className="flex items-end gap-2">
                    <p className={`text-2xl font-black tracking-tight ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                      {formatCurrency(selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel))}
                    </p>
                  </div>
                  <p className={`text-xs font-medium mt-1.5 ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                    {selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? '✓ Caisse parfaitement équilibrée'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? '↑ Excédent (Plus)'
                      : '↓ Manque (Moins)'}
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="admin_notes" className="text-sm font-semibold text-gray-700">Notes Admin</Label>
                <div className="relative">
                  <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    id="admin_notes"
                    value={adminFormData.notes}
                    onChange={(e) => setAdminFormData({ ...adminFormData, notes: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm text-sm min-h-[80px] resize-none"
                    placeholder="Commentaires de validation..."
                  />
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]">
                  <Calculator className="w-5 h-5 mr-2" />
                  Valider la Caisse
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Caisse;
